/**
 * The sentence for a Bin restore the server REFUSED for a reason it explains (#801 L4
 * OBS-2), or `null` for any other failure (the caller shows its generic one).
 *  - 403 MODIFICATION_SAFEGUARD (E4): the item holds, or would land in, an app the
 *    restorer can't change — it STAYS in the Bin. Neutral: the server names an app only to
 *    a restorer who can read the target, which a localized sentence cannot express.
 *  - 403 RESTORE_NEEDS_MANAGER / ACCESS_CHANGE_FORBIDDEN: needs someone who manages it.
 *  - 402 TENANT_WRITE_FROZEN (#801 round 8): uploads are paused — a restore re-creates
 *    the item, so the freeze refuses it. Localized from the CODE, the same branch the
 *    drive's banner takes (the server's sentence is English-only by design).
 *  - 413 STORAGE_QUOTA_EXCEEDED: the server's sentence, as every drive surface renders
 *    it (`storageRefusalText`).
 *  - #826, the item's folder is gone and the restore rebuilds it: 403 RESTORE_PATH_REFUSED
 *    (the restorer couldn't edit a folder it brings back — named by `folderName`, or the
 *    folder it goes into), 409 RESTORE_REBUILD_UNAVAILABLE (an app to restore first, or no
 *    rebuild for this restorer), 409 RESTORE_ANCESTOR_BUSY, 409 RESTORE_PLAN_CHANGED, and
 *    403 RESTORE_RELOCATION_OWNER_ONLY (only the owner restores a private item elsewhere),
 *    403 RESTORE_MERGE_REFUSED (it merges into a folder back in the drive the restorer can't write),
 *    409 RESTORE_NEWER_COPY (a newer Bin copy of a folder in it must be restored first).
 *    Localized from the code; the server's sentences are English.
 *  - 503 ENGINE_DB_VERIFYING (E7 PR 0b): an app database in it is still being checked — nothing was done, it STAYS in
 *    the Bin; no automatic retry (owner, option B), the person tries again in a moment.
 *  - 400 RESERVED_ROOT (#1119): a folder that would land at the top of the drive under a
 *    reserved name (`_shared`, `MiniApps`, …) — restoring it into another folder works.
 *    Review round 5: the sentence depends on what happens next (`reservedRootRestoreText`) —
 *    only a caller that OPENS the folder picker says "pick another folder"; otherwise the
 *    person is told to ask the drive owner. A rebuild's refusal names the reserved ANCESTOR
 *    it would recreate (the server's `reservedName`).
 */
import { TENANT_WRITE_FROZEN_CODE, storageRefusalText } from '@papan/types'

type T = (key: string, opts?: Record<string, unknown>) => string

/**
 * #1119 review round 5 — the sentence for a 400 RESERVED_ROOT restore. `pickerOpens`: the caller
 * opens the folder picker right after this toast (only then may it say "pick another folder").
 */
export function reservedRootRestoreText(t: T, err: unknown, name: string, pickerOpens: boolean): string {
  const reserved = (err as { reservedName?: unknown } | null)?.reservedName
  const folder = typeof reserved === 'string' && reserved ? reserved : null
  if (!pickerOpens) {
    return folder
      ? t('binView.toast.restoreReservedAncestorNoPicker', { name, folder })
      : t('binView.toast.restoreReservedNoRelocate', { name })
  }
  return folder
    ? t('binView.toast.restoreReservedAncestor', { name, folder })
    : t('binView.toast.restoreReservedName', { name })
}

export function restoreRefusalMessage(t: T, err: unknown, name: string, opts: { pickerOpens?: boolean } = {}): string | null {
  const e = err as { status?: number; code?: string; folderName?: string } | null
  if (e?.code === TENANT_WRITE_FROZEN_CODE) return t('binView.toast.restoreFrozen', { name })
  if (e?.code === 'ENGINE_DB_VERIFYING') return t('binView.toast.restoreDbVerifying')
  const storage = storageRefusalText(err)
  if (storage) return storage
  const folder = e?.folderName
  switch (e?.code) {
    case 'RESTORE_NEEDS_REBUILD':
      // A surface without the Bin's plan dialog (the Shared page): send them to the Bin, which
      // shows what the restore brings back.
      return t('binView.toast.restoreFromBin', { name })
    case 'RESTORE_PATH_REFUSED':
      return folder ? t('binView.toast.restorePathRefused', { name, folder }) : t('binView.toast.restorePathRefusedParent', { name })
    case 'RESTORE_REBUILD_UNAVAILABLE':
      return folder ? t('binView.toast.restoreAppFirst', { name, folder }) : t('binView.toast.restoreRebuildUnavailable', { name })
    case 'RESTORE_ANCESTOR_BUSY':
      return t('binView.toast.restoreAncestorBusy', { name, folder: folder ?? '' })
    case 'RESTORE_PLAN_CHANGED':
      return t('binView.toast.restorePlanChanged', { name })
    case 'RESTORE_ITEM_LIVE':
      return t('binView.toast.restoreItemLive', { name })
    case 'RESTORE_INNER_FOLDER_LIVE':
      return t('binView.toast.restoreInnerFolderLive', { name, folder: folder ?? '' })
    case 'RESTORE_NEWER_COPY':
      // Only someone who can see the newer copy is told to restore it; anyone else, who to ask.
      return t((e as { canRestoreNewer?: boolean }).canRestoreNewer ? 'binView.toast.restoreNewerCopy' : 'binView.toast.restoreNewerCopyHidden', { name, folder: folder ?? name })
    case 'RESTORE_MERGE_REFUSED':
      return t('binView.toast.restoreMergeRefused', { name, folder: folder ?? '' })
    case 'RESTORE_RELOCATION_OWNER_ONLY':
      return t('binView.toast.restoreOwnerOnly', { name })
    case 'RESERVED_ROOT':
      // #1119: it would land as a reserved top-level folder (`_shared`, `MiniApps`, …) — another folder works
      return reservedRootRestoreText(t, err, name, !!opts.pickerOpens)
  }
  if (e?.status !== 403) return null
  if (e.code === 'MODIFICATION_SAFEGUARD') return t('binView.toast.restoreSafeguard', { name })
  if (e.code === 'RESTORE_NEEDS_MANAGER' || e.code === 'ACCESS_CHANGE_FORBIDDEN') return t('binView.toast.restoreNeedsManager', { name })
  return null
}
